import { useIsMutating } from '@/api/hooks';
import {
  CascaderField,
  DatePickerField,
  Form,
  type FormInstance,
  Loading,
  Modal,
  type OperationType,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useMyOrganizationOptions } from '@/features/authentication/_hooks/me';
import { useForm } from '@/hooks';
import { getCurrentFiscalYear } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { GroupFormSchemaForRs } from '../_models/group';

type FormValues = z.infer<typeof GroupFormSchemaForRs>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  isLoading: boolean;
  onSubmit: (values: FormValues) => void;
  open: boolean;
  onClose: () => void;
};

export const ProjectGroupFormModal: FC<Props> = ({
  type,
  initialValues,
  isLoading,
  onSubmit,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const ref = useRef<FormInstance>(null);
  const mutationCount = useIsMutating();

  return (
    <Modal
      open={open}
      title={
        type === 'edit' ? t('edit-project-group') : t('create-project-group')
      }
      footer={
        <SubmitButton
          loading={mutationCount > 0}
          disabled={isLoading}
          onClick={() => ref.current?.submit()}
        />
      }
      onCancel={onClose}
      destroyOnClose
    >
      {isLoading ? (
        <Loading />
      ) : (
        <ProjectGroupForm
          ref={ref}
          type={type}
          initialValues={initialValues}
          onSubmit={onSubmit}
        />
      )}
    </Modal>
  );
};

const ProjectGroupForm = forwardRef<
  FormInstance,
  Omit<Props, 'isLoading' | 'open' | 'onClose'>
>(({ type, initialValues, onSubmit }, ref) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      fiscalYear: dayjs().year(getCurrentFiscalYear()),
      ...initialValues,
    },
    resolver: zodResolver(GroupFormSchemaForRs),
  });
  const organizationOptions = useMyOrganizationOptions();

  return (
    <Form
      leavePageAlertCondition={null}
      ref={ref}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <TextInputField
        name="name"
        autocompleteKey="group.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />

      <DatePickerField
        name="fiscalYear"
        label={t('created-fiscal-year')}
        picker="year"
        control={control}
        errors={errors}
        disabled={type === 'edit'}
        required
      />

      <CascaderField
        name="organizationIds"
        label={t('organization')}
        control={control}
        errors={errors}
        options={organizationOptions.data}
        disabled={type === 'edit'}
        loading={organizationOptions.isLoading}
        required
      />
    </Form>
  );
});
